import React, { useState } from 'react';
import { 
  runTournamentGames, 
  runGamesInParallel, 
  clearMoveCache, 
  getCacheStats 
} from '../config/oracle-ai';
import './TournamentOptimizer.css';

const TournamentOptimizer = ({ onBackToGame }) => {
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState(null);
  const [config, setConfig] = useState({
    numGames: 10,
    maxConcurrent: 5,
    boardSize: 7,
    pattern: 'skinny',
    player1: 'harary',
    player2: 'fastfrank'
  });

  const handleConfigChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const runOptimizedTournament = async () => {
    setIsRunning(true);
    setResults(null);
    
    try {
      // Clear cache before starting
      clearMoveCache();
      
      // Generate game configurations
      const gameConfigs = Array(config.numGames).fill().map(() => ({
        boardSize: config.boardSize,
        pattern: config.pattern,
        player1Key: config.player1,
        player2Key: config.player2,
        maxMoves: 50
      }));
      
      console.log(`Starting optimized tournament: ${config.numGames} games, ${config.maxConcurrent} concurrent`);
      const startTime = Date.now();
      
      // Run games using tournament optimization
      const gameResults = await runTournamentGames(gameConfigs, config.maxConcurrent);
      
      const endTime = Date.now();
      const duration = (endTime - startTime) / 1000;
      
      // Analyze results
      const player1Wins = gameResults.filter(r => r.winner === 'X').length;
      const player2Wins = gameResults.filter(r => r.winner === 'O').length;
      const draws = gameResults.filter(r => r.winner === 'draw').length;
      
      // Get cache stats
      const cacheStats = getCacheStats();
      
      setResults({
        totalGames: gameResults.length,
        player1Wins,
        player2Wins,
        draws,
        duration,
        cacheStats,
        gameResults
      });
      
    } catch (error) {
      console.error('Tournament failed:', error);
      setResults({ error: error.message });
    } finally {
      setIsRunning(false);
    }
  };

  const runStandardTournament = async () => {
    setIsRunning(true);
    setResults(null);
    
    try {
      // Clear cache before starting
      clearMoveCache();
      
      // Generate game configurations
      const gameConfigs = Array(config.numGames).fill().map(() => ({
        boardSize: config.boardSize,
        pattern: config.pattern,
        player1Key: config.player1,
        player2Key: config.player2,
        maxMoves: 50
      }));
      
      console.log(`Starting standard tournament: ${config.numGames} games, ${config.maxConcurrent} concurrent`);
      const startTime = Date.now();
      
      // Run games using standard parallel processing
      const gameResults = await runGamesInParallel(gameConfigs, config.maxConcurrent);
      
      const endTime = Date.now();
      const duration = (endTime - startTime) / 1000;
      
      // Analyze results
      const player1Wins = gameResults.filter(r => r.winner === 'X').length;
      const player2Wins = gameResults.filter(r => r.winner === 'O').length;
      const draws = gameResults.filter(r => r.winner === 'draw').length;
      
      // Get cache stats
      const cacheStats = getCacheStats();
      
      setResults({
        totalGames: gameResults.length,
        player1Wins,
        player2Wins,
        draws,
        duration,
        cacheStats,
        gameResults
      });
      
    } catch (error) {
      console.error('Tournament failed:', error);
      setResults({ error: error.message });
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className="tournament-optimizer">
      <div className="optimizer-header">
        <h2>Tournament Optimizer</h2>
        <button onClick={onBackToGame} className="back-button">
          ← Back to Game
        </button>
      </div>

      <div className="optimizer-container">
        <div className="config-section">
          <h3>Tournament Configuration</h3>
          <div className="config-grid">
            <div className="config-item">
              <label>Number of Games:</label>
              <input
                type="number"
                value={config.numGames}
                onChange={(e) => handleConfigChange('numGames', parseInt(e.target.value))}
                min="1"
                max="100"
                disabled={isRunning}
              />
            </div>
            
            <div className="config-item">
              <label>Max Concurrent:</label>
              <input
                type="number"
                value={config.maxConcurrent}
                onChange={(e) => handleConfigChange('maxConcurrent', parseInt(e.target.value))}
                min="1"
                max="20"
                disabled={isRunning}
              />
            </div>
            
            <div className="config-item">
              <label>Board Size:</label>
              <select
                value={config.boardSize}
                onChange={(e) => handleConfigChange('boardSize', parseInt(e.target.value))}
                disabled={isRunning}
              >
                <option value={6}>6x6</option>
                <option value={7}>7x7</option>
                <option value={8}>8x8</option>
              </select>
            </div>
            
            <div className="config-item">
              <label>Pattern:</label>
              <select
                value={config.pattern}
                onChange={(e) => handleConfigChange('pattern', e.target.value)}
                disabled={isRunning}
              >
                <option value="skinny">Skinny</option>
                <option value="boxy">Boxy</option>
                <option value="el">El</option>
                <option value="elly">Elly</option>
              </select>
            </div>
            
            <div className="config-item">
              <label>Player 1:</label>
              <select
                value={config.player1}
                onChange={(e) => handleConfigChange('player1', e.target.value)}
                disabled={isRunning}
              >
                <option value="harary">Harary</option>
                <option value="fastfrank">Fast Frank</option>
                <option value="erdos">Erdos</option>
              </select>
            </div>
            
            <div className="config-item">
              <label>Player 2:</label>
              <select
                value={config.player2}
                onChange={(e) => handleConfigChange('player2', e.target.value)}
                disabled={isRunning}
              >
                <option value="fastfrank">Fast Frank</option>
                <option value="harary">Harary</option>
                <option value="erdos">Erdos</option>
              </select>
            </div>
          </div>
        </div>

        <div className="controls-section">
          <h3>Run Tournament</h3>
          <div className="button-group">
            <button
              onClick={runOptimizedTournament}
              disabled={isRunning}
              className="run-button optimized"
            >
              {isRunning ? 'Running...' : 'Run Optimized Tournament'}
            </button>
            
            <button
              onClick={runStandardTournament}
              disabled={isRunning}
              className="run-button standard"
            >
              {isRunning ? 'Running...' : 'Run Standard Tournament'}
            </button>
            
            <button
              onClick={clearMoveCache}
              disabled={isRunning}
              className="clear-cache-button"
            >
              Clear Cache
            </button>
          </div>
        </div>

        {results && (
          <div className="results-section">
            <h3>Tournament Results</h3>
            {results.error ? (
              <div className="error-message">{results.error}</div>
            ) : (
              <div className="results-grid">
                <div className="result-item">
                  <span className="result-label">Total Games:</span>
                  <span className="result-value">{results.totalGames}</span>
                </div>
                
                <div className="result-item">
                  <span className="result-label">Duration:</span>
                  <span className="result-value">{results.duration.toFixed(2)}s</span>
                </div>
                
                <div className="result-item">
                  <span className="result-label">Games/Second:</span>
                  <span className="result-value">{(results.totalGames / results.duration).toFixed(2)}</span>
                </div>
                
                <div className="result-item">
                  <span className="result-label">{config.player1} Wins:</span>
                  <span className="result-value">{results.player1Wins}</span>
                </div>
                
                <div className="result-item">
                  <span className="result-label">{config.player2} Wins:</span>
                  <span className="result-value">{results.player2Wins}</span>
                </div>
                
                <div className="result-item">
                  <span className="result-label">Draws:</span>
                  <span className="result-value">{results.draws}</span>
                </div>
                
                <div className="result-item">
                  <span className="result-label">Cache Hits:</span>
                  <span className="result-value">{results.cacheStats.size}</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TournamentOptimizer;
